import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Printer, FileCheck, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { toast } from 'sonner';

/**
 * iter896 — VAT Return Draft dialog.
 *
 * Bridges the VAT Summary page to the country-specific official return
 * template. Calls the existing `/accounting/vat-return` endpoint which
 * already knows how to shape the numbers into ZATCA (KSA) / HMRC (UK) /
 * generic box layouts, then renders them in a modal that mirrors what
 * the operator sees on the tax authority portal — so filing is a copy-
 * paste (or Export PDF → attach) exercise.
 */
export default function VATReturnDraftDialog({ open, onOpenChange, month, branchId, monthLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [year, mo] = (month || '').split('-');
  const startDate = year ? `${year}-${mo}-01` : '';
  const endDate = useMemo(() => {
    if (!year) return '';
    const next = new Date(+year, +mo, 1); // first of next month
    next.setDate(0); // last day of target month
    return next.toISOString().slice(0, 10);
  }, [year, mo]);

  useEffect(() => {
    if (!open || !startDate) return;
    setLoading(true);
    setData(null);
    const params = { start_date: startDate, end_date: endDate };
    if (branchId && branchId !== 'all') params.branch_id = branchId;
    api.get('/accounting/vat-return', { params })
      .then(({ data: res }) => setData(res))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load VAT return draft'))
      .finally(() => setLoading(false));
  }, [open, startDate, endDate, branchId]);

  const country = data?.country_name || '';
  const countryCode = data?.country || '';
  const boxes = data?.boxes || [];
  const summary = data?.summary || {};
  const mtd = data?.mtd || null;
  const excise = data?.excise || null;

  // Group boxes into Output / Input / Net / Excise for a cleaner official look.
  const outputBoxes = boxes.filter(b => b.type?.startsWith('output') || b.type === 'exempt');
  const inputBoxes = boxes.filter(b => b.type?.startsWith('input') || b.type === 'adjustment');
  const netBoxes = boxes.filter(b => b.type === 'net');
  const exciseBoxes = boxes.filter(b => b.type?.startsWith('excise'));

  const handleExportPdf = async () => {
    if (!data) return;
    const rows = (arr) => arr.map(b => `
      <tr>
        <td class="box-num">${b.box}</td>
        <td>${b.label}</td>
        <td class="num">${(b.amount || 0).toLocaleString()}</td>
        <td class="num">${(b.vat || 0).toLocaleString()}</td>
      </tr>`).join('');

    const htmlFallback = `<html><head><title>Draft VAT Return — ${country} — ${monthLabel}</title>
<style>
  body{font-family:'Helvetica Neue',Arial,sans-serif;padding:36px;color:#111;font-size:12px}
  h1{margin:0 0 4px;font-size:22px}
  .sub{color:#666;font-size:11px;margin-bottom:16px}
  .draft-banner{background:#fef3c7;border:1px dashed #d97706;padding:10px 14px;border-radius:8px;font-size:11px;color:#92400e;margin-bottom:18px;text-align:center}
  h2{font-size:13px;text-transform:uppercase;letter-spacing:.06em;margin:16px 0 4px;color:#333;border-bottom:2px solid #333;padding-bottom:4px}
  table{width:100%;border-collapse:collapse;margin-bottom:16px}
  th,td{border-bottom:1px solid #ddd;padding:8px 10px;text-align:left;font-size:11px}
  th{background:#f8f8f8;font-weight:600;text-transform:uppercase;font-size:9px;letter-spacing:.05em;color:#555}
  td.num,th.num{text-align:right;font-variant-numeric:tabular-nums}
  td.box-num{font-family:monospace;color:#666;width:52px}
  .net{background:#f0fdf4;border:2px solid #0b8f45;padding:14px;border-radius:8px;margin-top:16px}
  .net .lbl{font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#555}
  .net .val{font-size:22px;font-weight:700;color:#0b8f45}
  .footer{margin-top:24px;padding-top:8px;border-top:1px solid #eee;font-size:9px;color:#999;text-align:center}
</style></head><body>
<h1>Draft VAT Return</h1>
<div class="sub">${country} · ${monthLabel} · Generated ${new Date().toLocaleString()}</div>
<div class="draft-banner">⚠ DRAFT — Cross-check every box against your source documents before filing on the tax authority portal.</div>
<h2>Output VAT (Sales)</h2>
<table><thead><tr><th>Box</th><th>Description</th><th class="num">Amount</th><th class="num">VAT</th></tr></thead><tbody>${rows(outputBoxes)}</tbody></table>
<h2>Input VAT (Purchases)</h2>
<table><thead><tr><th>Box</th><th>Description</th><th class="num">Amount</th><th class="num">VAT</th></tr></thead><tbody>${rows(inputBoxes)}</tbody></table>
${netBoxes.length ? `<h2>Net VAT</h2><table><thead><tr><th>Box</th><th>Description</th><th class="num">Amount</th><th class="num">VAT</th></tr></thead><tbody>${rows(netBoxes)}</tbody></table>` : ''}
<div class="net">
  <div class="lbl">Net VAT ${summary.status?.toUpperCase() || ''}</div>
  <div class="val">${cur()} ${Math.abs(summary.net_vat || 0).toLocaleString()}</div>
</div>
<div class="footer">Powered by SMARTO ERP · Draft VAT Return · Not a substitute for the official filing on the tax authority portal.</div>
<script>window.onload=()=>{setTimeout(()=>window.print(),150)}</script>
</body></html>`;

    const { printSummary } = await import('@/lib/printSummary');
    let printers = [];
    try { const { data: p } = await api.get('/printers'); printers = Array.isArray(p) ? p : (p?.items || []); } catch { /* no printers OK */ }
    await printSummary({
      printers,
      branchId: branchId !== 'all' ? branchId : null,
      title: `Draft VAT Return ${monthLabel}`,
      htmlFallback,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="vat-return-draft-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold font-outfit flex items-center gap-2">
            <FileCheck size={20} className="text-emerald-600" />
            Draft VAT Return
            {country && <Badge variant="outline" className="ml-1 text-[10px]">{country}</Badge>}
            {mtd?.mtd_ready && (
              <Badge className="ml-1 text-[10px] bg-emerald-600 text-white hover:bg-emerald-700" data-testid="mtd-ready-badge">
                MTD Ready
              </Badge>
            )}
            {mtd && !mtd.mtd_ready && (
              <Badge variant="outline" className="ml-1 text-[10px] border-amber-400 text-amber-700" data-testid="mtd-not-ready-badge">
                MTD Draft
              </Badge>
            )}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Auto-filled from the {monthLabel} VAT Summary. Cross-check every box before submitting to the tax authority portal.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="animate-spin mr-2" size={18} /> Building the draft…
          </div>
        ) : !data ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
            No data available for this period.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="vat-return-draft-body">
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/25 border border-dashed border-amber-400/60 dark:border-amber-700/40 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300 flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span><b>Draft</b> — Not filed. Copy the values below into your tax portal or Export PDF and attach to your filing.</span>
            </div>

            {[
              { title: 'Output VAT (Sales)', rows: outputBoxes, accent: 'emerald' },
              { title: 'Input VAT (Purchases)', rows: inputBoxes, accent: 'amber' },
              netBoxes.length ? { title: 'Net VAT', rows: netBoxes, accent: 'sky' } : null,
              exciseBoxes.length ? { title: 'Excise Goods (info only)', rows: exciseBoxes, accent: 'rose' } : null,
            ].filter(Boolean).map((section) => (
              <div key={section.title} className="border rounded-xl overflow-hidden">
                <div className={`px-4 py-2 border-b bg-${section.accent}-50/40 dark:bg-${section.accent}-950/15`}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{section.title}</div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b text-xs">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground w-16">Box</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Description</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Amount</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">VAT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((b, i) => (
                      <tr key={b.box} className="border-b hover:bg-muted/20" data-testid={`vat-return-box-${b.box}`}>
                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{b.box}</td>
                        <td className="px-3 py-2">{b.label}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{(b.amount || 0).toLocaleString()}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold text-${section.accent}-700`}>{(b.vat || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/20 p-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-400">
                  Net VAT {summary.status?.toUpperCase() || 'DUE'}
                </div>
                <div className="text-2xl font-bold text-emerald-800 dark:text-emerald-300 mt-1" data-testid="vat-return-net-total">
                  {cur()} {Math.abs(summary.net_vat || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <FileCheck size={40} className="text-emerald-500/50" />
            </div>

            {mtd && (
              <div className="rounded-xl border border-sky-300 bg-sky-50/60 dark:bg-sky-950/20 p-3" data-testid="mtd-panel">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-sky-700 dark:text-sky-400">
                    HMRC Making Tax Digital (MTD)
                  </div>
                  {mtd.mtd_ready ? (
                    <Badge className="text-[10px] bg-emerald-600 text-white">Ready to submit</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">Draft only</Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div><span className="text-muted-foreground">Period key:</span> <span className="font-mono font-semibold" data-testid="mtd-period-key">{mtd.period_key || '—'}</span></div>
                  <div><span className="text-muted-foreground">VRN:</span> <span className="font-mono font-semibold">{mtd.vrn || 'Not set'}</span></div>
                  <div><span className="text-muted-foreground">Digital links:</span> <span className="text-emerald-700 font-semibold">{mtd.digital_links ? '✓ Yes' : '✗ No'}</span></div>
                  <div><span className="text-muted-foreground">Retention:</span> <span className="font-semibold">{mtd.record_retention_years} yrs</span></div>
                </div>
                <div className="mt-2 text-[10px] text-sky-800 dark:text-sky-300 leading-relaxed">
                  {mtd.hmrc_notice}
                </div>
              </div>
            )}

            {excise && (excise.excise_net > 0 || excise.excise_tax > 0) && (
              <div className="rounded-xl border border-rose-300 bg-rose-50/60 dark:bg-rose-950/20 p-3" data-testid="excise-panel">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-700 dark:text-rose-400">
                    UAE Excise Tax (info)
                  </div>
                  <Badge variant="outline" className="text-[10px] border-rose-400 text-rose-700">Separate return</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div><span className="text-muted-foreground">Excise supplies (net):</span> <span className="font-semibold tabular-nums" data-testid="excise-net">{excise.excise_net.toLocaleString()}</span></div>
                  <div><span className="text-muted-foreground">Excise tax due:</span> <span className="font-semibold tabular-nums text-rose-700" data-testid="excise-tax">{excise.excise_tax.toLocaleString()}</span></div>
                </div>
                <div className="mt-2 text-[10px] text-rose-800 dark:text-rose-300 leading-relaxed">
                  {excise.note}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="vat-return-draft-close">Close</Button>
          <Button onClick={handleExportPdf} disabled={!data || loading} className="gap-1.5" data-testid="vat-return-draft-export">
            <Printer size={14} /> Export PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
